import { UNIT_LABELS, UnitSystem } from "types/units";

export const FIELD_NAMES = {
  HZ_LENGTH: "Hz Length",
  STAGE_SPACING: "Stage Spacing",
  PROPPANT_INTENSITY: "Proppant Intensity"
};

/**
 * Build a field label with dynamic unit based on user preference
 * @param fieldName - The field name without units
 * @param siUnit - The SI unit label key from UNIT_LABELS (e.g., "METERS", "TONNES")
 * @param unitType - User's unit preference (0=Hybrid, 1=SI, 2=Imperial)
 * @returns Field label with appropriate unit (e.g., "Hz Length (m)" or "Hz Length (ft)")
 */
export const buildFieldLabel = (
  fieldName: string,
  siUnit: keyof typeof UNIT_LABELS,
  imperialUnit: keyof typeof UNIT_LABELS,
  unitType: number
): string => {
  const unit =
    unitType === UnitSystem.Imperial ? UNIT_LABELS[imperialUnit] : UNIT_LABELS[siUnit];
  return `${fieldName} (${unit})`;
};

/**
 * Field label generators with dynamic units based on user preference
 */
export const FIELD_LABELS = {
  /**
   * Generic method to get any distance-related field label
   * @param fieldName - The field name without units (e.g., "Depth", "Lateral Length")
   * @param unitType - User's unit preference
   * @returns Field label with m or ft
   */
  getDistanceLabel: (fieldName: string, unitType: number): string => {
    return buildFieldLabel(fieldName, "METERS", "FEET", unitType);
  },

  /**
   * Generic method to get any area-related field label
   * @param fieldName - The field name without units
   * @param unitType - User's unit preference
   * @returns Field label with m² or ft²
   */
  getAreaLabel: (fieldName: string, unitType: number): string => {
    return buildFieldLabel(fieldName, "SQUARE_METERS", "SQUARE_FEET", unitType);
  },

  /**
   * Generic method to get any volume-related field label
   * @param fieldName - The field name without units
   * @param unitType - User's unit preference
   * @returns Field label with m³ or ft³
   */
  getVolumeLabel: (fieldName: string, unitType: number): string => {
    return buildFieldLabel(fieldName, "CUBIC_METERS", "CUBIC_FEET", unitType);
  },

  /**
   * Generic method to get any pressure-related field label
   * @param fieldName - The field name without units
   * @param unitType - User's unit preference
   * @returns Field label with kPa or psi
   */
  getPressureLabel: (fieldName: string, unitType: number): string => {
    return buildFieldLabel(fieldName, "KILOPASCALS", "PSI", unitType);
  },

  /**
   * Generic method to get any mass-related field label
   * @param fieldName - The field name without units
   * @param unitType - User's unit preference
   * @returns Field label with t or ton
   */
  getMassLabel: (fieldName: string, unitType: number): string => {
    return buildFieldLabel(fieldName, "TONNES", "TONS", unitType);
  }
};

/**
 * Field label functions with dynamic units - for use in components
 * Call these functions with unitType to get the appropriate label
 */
export const FIELD_LABEL_GETTERS = {
  HZ_LENGTH: (unitType: number) =>
    FIELD_LABELS.getDistanceLabel(FIELD_NAMES.HZ_LENGTH, unitType),
  STAGE_SPACING: (unitType: number) =>
    FIELD_LABELS.getDistanceLabel(FIELD_NAMES.STAGE_SPACING, unitType),
  PROPPANT_INTENSITY: (unitType: number) =>
    unitType === UnitSystem.Imperial
      ? `${FIELD_NAMES.PROPPANT_INTENSITY} (lb/ft)`
      : `${FIELD_NAMES.PROPPANT_INTENSITY} (t/m)`
};
